import { useState, useEffect } from 'react'
import { useStore } from '../lib/store'
import { api } from '../lib/api'
import { Moon, Sun, Trash2, CreditCard, CheckCircle2 } from 'lucide-react'
import { resetTour } from '../components/onboarding/CoachMarks'

function SubscriptionSection() {
  const [status, setStatus] = useState<any>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('subscription') === 'success') {
      // Just came back from Stripe checkout — sync subscription
      api.syncSubscription().then(() => {
        api.getBillingStatus().then(setStatus).catch(() => {})
      }).catch(() => {
        api.getBillingStatus().then(setStatus).catch(() => {})
      })
      // Clean up URL
      window.history.replaceState({}, '', '/settings')
    } else {
      api.getBillingStatus().then(setStatus).catch(() => {})
    }
  }, [])

  if (!status) return <p className="text-sm text-gray-500">Loading...</p>

  const tier = status.tier || 'core'

  const handleCheckout = async (priceLookupKey: string) => {
    setLoading(priceLookupKey)
    setError(null)
    try {
      const { checkout_url } = await api.createCheckout(priceLookupKey)
      window.location.href = checkout_url
    } catch (e: any) {
      setLoading(null)
      if (e.message?.includes('already have an active')) {
        api.syncSubscription().then(() => {
          api.getBillingStatus().then(setStatus)
        })
      } else {
        setError(e.message || 'Failed to start checkout')
      }
    }
  }

  const handleManage = async () => {
    try {
      const { portal_url } = await api.createPortalSession()
      window.location.href = portal_url
    } catch (e) { console.error(e) }
  }

  if (status.subscription_status === 'active' || status.subscription_status === 'canceling') {
    const isPro = tier === 'pro'
    const planPrice = isPro ? '$199/month' : '$99/month'
    const cancelLabel = isPro ? 'Pro (Surgical) Plan — Canceling' : 'Core Plan — Canceling'
    const activeLabel = isPro ? 'Pro (Surgical) Plan — Active' : 'Core Plan — Active'
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-medium">
            {status.subscription_status === 'canceling' ? cancelLabel : activeLabel}
          </span>
        </div>
        <p className="text-sm text-gray-500">
          {status.subscription_status === 'canceling'
            ? `${planPrice} · Cancels at end of billing period`
            : `${planPrice} · Unlimited encounters`}
        </p>
        {!isPro && (
          <button
            onClick={() => handleCheckout('pro_monthly')}
            disabled={loading !== null}
            className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
          >
            {loading === 'pro_monthly' ? 'Loading...' : 'Upgrade to Pro — $199/mo'}
          </button>
        )}
        <button onClick={handleManage} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
          <CreditCard className="w-4 h-4" /> Manage Subscription
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-600 dark:text-gray-400">
        <span className="font-medium text-gray-900 dark:text-gray-200">Free Trial</span>
        {' — '}{status.trial_remaining} of {status.trial_encounters_limit} encounters remaining
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => handleCheckout('core_monthly')}
          disabled={loading !== null}
          className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading === 'core_monthly' ? 'Loading...' : 'Upgrade to Core — $99/mo'}
        </button>
        <button
          onClick={() => handleCheckout('pro_monthly')}
          disabled={loading !== null}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading === 'pro_monthly' ? 'Loading...' : 'Upgrade to Pro — $199/mo'}
        </button>
      </div>
    </div>
  )
}

const SPECIALTIES = [
  'Orthopedic Surgery',
  'Spine Surgery',
  'General Surgery',
  'Neurosurgery',
  'Cardiothoracic Surgery',
  'Vascular Surgery',
  'Plastic Surgery',
  'Urology',
  'ENT / Otolaryngology',
  'Ophthalmology',
  'OB/GYN',
  'Family Medicine',
  'Internal Medicine',
  'Pediatrics',
  'Emergency Medicine',
  'Psychiatry',
  'Dermatology',
  'Cardiology',
  'Pulmonology',
  'Gastroenterology',
  'Rheumatology',
  'Endocrinology',
  'Neurology',
  'Oncology',
  'Physical Medicine & Rehab',
  'Pain Management',
  'Nephrology',
  'Hematology',
  'Infectious Disease',
  'Allergy & Immunology',
  'Other',
]

const EMR_SYSTEMS = [
  'Epic',
  'Cerner/Oracle Health',
  'athenahealth',
  'eClinicalWorks',
  'Allscripts',
  'NextGen',
  'DrChrono',
  'ModMed',
  'Practice Fusion',
  'AdvancedMD',
  'Greenway',
  'Other',
  'None',
]

export function SettingsPage() {
  const { doctor, logout, logoutAllSessions, updateDoctor } = useStore()
  
  // Profile state
  const [name, setName] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [currentEmr, setCurrentEmr] = useState('')
  const [profileEdited, setProfileEdited] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState(false)

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('scribey_theme') === 'dark')

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Phase 2: retention window (days). null/undefined => default 30; 0 => never.
  const [retentionDays, setRetentionDays] = useState<number>(30)
  const [savingRetention, setSavingRetention] = useState(false)
  const [retentionSaved, setRetentionSaved] = useState(false)

  useEffect(() => {
    if (doctor) {
      setName(doctor.name || '')
      setSpecialty(doctor.specialty || '')
      setCurrentEmr((doctor as any).settings?.current_emr || '')
      const rd = (doctor as any).transcript_retention_days
      setRetentionDays(rd === null || rd === undefined ? 30 : rd)
    }
    
    // Sync dark mode state with what the boot script already applied
    const savedTheme = localStorage.getItem('scribey_theme')
    setDarkMode(savedTheme === 'dark')
  }, [doctor])

  useEffect(() => {
    if (doctor) {
      const changed = 
        name !== doctor.name ||
        specialty !== doctor.specialty ||
        currentEmr !== ((doctor as any).settings?.current_emr || '')
      setProfileEdited(changed)
    }
  }, [name, specialty, currentEmr, doctor])

  async function handleSaveRetention(days: number) {
    setRetentionDays(days)
    setSavingRetention(true)
    setRetentionSaved(false)
    try {
      const updated = await api.updateProfile({ transcript_retention_days: days })
      updateDoctor(updated)
      setRetentionSaved(true)
      setTimeout(() => setRetentionSaved(false), 2500)
    } catch (err) {
      console.error('Failed to save retention setting:', err)
    } finally {
      setSavingRetention(false)
    }
  }

  async function handleSaveProfile() {
    setSavingProfile(true)
    setProfileError('')
    setProfileSuccess(false)
    
    try {
      const updated = await api.updateProfile({
        name: name !== doctor?.name ? name : undefined,
        specialty: specialty !== doctor?.specialty ? specialty : undefined,
        current_emr: currentEmr !== ((doctor as any)?.settings?.current_emr || '') ? currentEmr : undefined,
      })
      updateDoctor(updated)
      setProfileSuccess(true)
      setProfileEdited(false)
      setTimeout(() => setProfileSuccess(false), 3000)
    } catch (err: any) {
      setProfileError(err.message || 'Failed to update profile')
    } finally {
      setSavingProfile(false)
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }

    setChangingPassword(true)
    try {
      await api.changePassword(currentPassword, newPassword)
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordSuccess(false), 3000)
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password')
    } finally {
      setChangingPassword(false)
    }
  }

  function toggleDarkMode() {
    const newMode = !darkMode
    setDarkMode(newMode)
    
    if (newMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('scribey_theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('scribey_theme', 'light')
    }
  }

  if (!doctor) return null

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>

      {/* Profile */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Profile</h2>
        
        {profileError && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg p-3 mb-4">
            {profileError}
          </div>
        )}
        
        {profileSuccess && (
          <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-600 dark:text-green-300 text-sm rounded-lg p-3 mb-4">
            Profile updated successfully
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm flex items-center justify-between">
              <span>{doctor.email}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">(cannot be changed)</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Specialty</label>
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">Select specialty</option>
              {SPECIALTIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current EMR</label>
            <select
              value={currentEmr}
              onChange={(e) => setCurrentEmr(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">Select EMR system</option>
              {EMR_SYSTEMS.map((emr) => (
                <option key={emr} value={emr}>{emr}</option>
              ))}
            </select>
          </div>

          {profileEdited && (
            <button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Change Password</h2>
        
        {passwordError && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg p-3 mb-4">
            {passwordError}
          </div>
        )}
        
        {passwordSuccess && (
          <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-600 dark:text-green-300 text-sm rounded-lg p-3 mb-4">
            Password changed successfully
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Min. 8 characters"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Re-enter new password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={changingPassword}
            className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Active Sessions */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Active Sessions</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Sign out everywhere — including any shared or public computers you approved.
          Use this if you forgot to sign out of a nursing-station or exam-room computer.
        </p>
        <button
          onClick={async () => {
            if (!confirm('Sign out of ALL devices, including this one? You will need to sign in again.')) return
            await logoutAllSessions()
          }}
          className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          Sign out all devices
        </button>
      </div>

      {/* Appearance */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Appearance</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Dark Mode</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Toggle between light and dark themes</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className={`relative w-14 h-8 rounded-full transition-colors ${
              darkMode ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full flex items-center justify-center transition-transform ${
                darkMode ? 'translate-x-6' : 'translate-x-0'
              }`}
            >
              {darkMode ? <Moon size={14} className="text-brand-600" /> : <Sun size={14} className="text-gray-600" />}
            </div>
          </button>
        </div>
      </div>

      {/* Privacy & Data Retention (Phase 2) */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Privacy &amp; Data Retention</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Raw transcripts and AI quality flags are automatically deleted after this window. Your signed notes are always kept &mdash; only the underlying transcript and flags are purged. Audio is never stored.
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { v: 30, label: '30 days' },
            { v: 60, label: '60 days' },
            { v: 90, label: '90 days' },
            { v: 0, label: 'Never' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => handleSaveRetention(opt.v)}
              disabled={savingRetention}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                retentionDays === opt.v
                  ? 'bg-brand-500 border-brand-500 text-white'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-brand-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          {retentionDays === 0
            ? 'Transcripts and flags will be retained indefinitely. You remain responsible for any applicable retention limits.'
            : `Transcripts and flags older than ${retentionDays} days are purged automatically. Default is 30 days.`}
          {retentionSaved && <span className="ml-2 text-emerald-500">✓ Saved</span>}
        </p>
      </div>

      {/* Account */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Account</h2>
        <div className="space-y-3">
          <button
            onClick={logout}
            className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Sign Out
          </button>
          
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
            <button
              onClick={async () => {
                try {
                  const data = await api.exportAccountData()
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = 'drscribey-export.json'
                  a.click()
                  URL.revokeObjectURL(url)
                } catch (err: any) {
                  alert(`Export failed: ${err.message}`)
                }
              }}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm transition-colors"
            >
              Download My Data (JSON)
            </button>
            <div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 text-sm transition-colors"
              >
                <Trash2 size={14} />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Subscription</h2>
        <SubscriptionSection />
      </div>

      {/* About */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">About</h2>
        <div className="space-y-3 text-sm">
          <p className="text-gray-700 dark:text-gray-300">Dr. Scribey — AI Medical Scribe</p>
          <p className="text-gray-500 dark:text-gray-400">Version 2.0.0-beta</p>
          <a href="/quickstart" className="inline-block text-brand-500 hover:text-brand-400 transition-colors">View Quickstart Guide →</a>
          <button
            onClick={() => { resetTour(); window.location.href = '/' }}
            className="block text-brand-500 hover:text-brand-400 transition-colors"
          >
            Replay app tour →
          </button>
          <div className="flex gap-3 text-gray-400 dark:text-gray-500">
            <a href="#" className="hover:text-brand-400 transition-colors">Terms of Service</a>
            <span>•</span>
            <a href="#" className="hover:text-brand-400 transition-colors">Privacy Policy</a>
            <span>•</span>
            <a href="#" className="hover:text-brand-400 transition-colors">BAA</a>
          </div>
          <p className="text-gray-400 dark:text-gray-500">© 2026 TailwindX, LLC</p>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">Delete Account</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Are you sure you want to delete your account? This action cannot be undone. All your data, including patients, encounters, and notes will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await api.deleteAccount()
                    setShowDeleteConfirm(false)
                    logout()
                  } catch (err: any) {
                    alert(`Account deletion failed: ${err.message}`)
                    setShowDeleteConfirm(false)
                  }
                }}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
